/**
 * FileInput — file picker with two visual variants.
 *
 *   • variant="button"   → Button-styled "Choose file" with native picker.
 *   • variant="dropzone" → Drag-and-drop area; click also opens picker.
 *
 * Design system mapping:
 *   • Dropzone box: border-dashed border-webex-gray-300 dark: webex-gray-600
 *                   bg-white dark:bg-webex-gray-950 rounded-lg p-6
 *   • On drag-over:  border-webex-blue-600 bg-webex-blue-100 dark:bg-webex-blue-900
 */
import * as React from "react"
import { cn } from "@lib/cn"
import { Button } from "./Button"

interface BaseProps {
  accept?: string
  multiple?: boolean
  disabled?: boolean
  onFilesSelected?: (files: File[]) => void
  className?: string
  name?: string
  required?: boolean
}

export interface FileInputProps extends BaseProps {
  variant?: "button" | "dropzone"
  buttonLabel?: React.ReactNode
  dropzoneLabel?: React.ReactNode
  dropzoneHint?: React.ReactNode
}

export const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      variant = "button",
      buttonLabel = "Choose file",
      dropzoneLabel = "Drop files here or click to browse",
      dropzoneHint,
      accept,
      multiple,
      disabled,
      onFilesSelected,
      className,
      name,
      required,
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)
    const [dragActive, setDragActive] = React.useState(false)
    const [fileNames, setFileNames] = React.useState<string[]>([])

    const handleFiles = (files: FileList | null) => {
      if (!files || files.length === 0) return
      const arr = Array.from(files)
      setFileNames(arr.map((f) => f.name))
      onFilesSelected?.(arr)
    }

    const hidden = (
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        name={name}
        required={required}
        aria-label={typeof buttonLabel === "string" ? buttonLabel : "Choose file"}
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
      />
    )

    if (variant === "button") {
      return (
        <span className={cn("inline-flex items-center gap-3", className)}>
          {hidden}
          <Button
            variant="secondary"
            size="default"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            {buttonLabel}
          </Button>
          <span className="text-sm text-webex-gray-700 dark:text-webex-gray-300 truncate max-w-[20ch]">
            {fileNames.length > 0 ? fileNames.join(", ") : "No file selected"}
          </span>
        </span>
      )
    }

    return (
      <div
        className={cn(
          "relative rounded-lg border border-dashed bg-white dark:bg-webex-gray-950 p-6",
          "border-webex-gray-300 dark:border-webex-gray-600",
          "transition-colors duration-150 motion-reduce:transition-none",
          "focus-within:ring-2 focus-within:ring-webex-blue-600 focus-within:ring-offset-2 focus-within:ring-offset-white dark:focus-within:ring-offset-webex-gray-950",
          dragActive && "border-webex-blue-600 bg-webex-blue-100 dark:bg-webex-blue-900",
          disabled && "opacity-50 cursor-not-allowed",
          className,
        )}
        onDragEnter={(e) => {
          e.preventDefault()
          if (!disabled) setDragActive(true)
        }}
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setDragActive(true)
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragActive(false)
          if (!disabled) handleFiles(e.dataTransfer.files)
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !disabled) {
            e.preventDefault()
            inputRef.current?.click()
          }
        }}
      >
        {hidden}
        <div className="text-center text-sm text-webex-gray-800 dark:text-webex-gray-200">
          {dropzoneLabel}
        </div>
        {dropzoneHint && (
          <div className="mt-1 text-center text-xs text-webex-gray-600 dark:text-webex-gray-400">
            {dropzoneHint}
          </div>
        )}
        {fileNames.length > 0 && (
          <ul className="mt-3 text-xs text-webex-gray-1000 dark:text-white space-y-0.5">
            {fileNames.map((n) => (
              <li key={n} className="truncate text-center">{n}</li>
            ))}
          </ul>
        )}
      </div>
    )
  },
)
FileInput.displayName = "FileInput"
